import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";

// 2026-10-06 · Confirmaciones y entradas con el diálogo de la app (nunca
// window.confirm/alert/prompt). Uso:
//   const { confirm, prompt } = useConfirm();
//   if (!(await confirm({ title, message, confirmText, danger }))) return;
//   if (!(await confirm({ ..., doubleConfirm: "Confirma de nuevo: …" }))) return;
//   const pax = await prompt({ title, label, defaultValue: "2", type: "number" });

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "¿Estás seguro?",
  message: "",
  confirmText: "Aceptar",
  cancelText: "Cancelar",
  danger: false,
  doubleConfirm: null,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { kind, opts, step, value }
  const resolver = useRef(null);

  const close = useCallback((result) => {
    const r = resolver.current;
    resolver.current = null;
    setState(null);
    if (r) r(result);
  }, []);

  const confirm = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        if (resolver.current) resolver.current(false);
        resolver.current = resolve;
        setState({ kind: "confirm", opts: { ...DEFAULTS, ...opts }, step: 1 });
      }),
    [],
  );

  const prompt = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        if (resolver.current) resolver.current(null);
        resolver.current = resolve;
        setState({
          kind: "prompt",
          opts: { ...DEFAULTS, title: "", ...opts },
          step: 1,
          value: opts.defaultValue ?? "",
        });
      }),
    [],
  );

  const onAccept = () => {
    if (!state) return;
    if (state.kind === "prompt") {
      close(state.value);
      return;
    }
    if (state.opts.doubleConfirm && state.step === 1) {
      setState({ ...state, step: 2 });
      return;
    }
    close(true);
  };

  const onCancel = () => close(state?.kind === "prompt" ? null : false);

  const opts = state?.opts || DEFAULTS;
  const message = state?.step === 2 ? state.opts.doubleConfirm : opts.message;

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog
        open={!!state}
        onClose={onCancel}
        maxWidth="xs"
        fullWidth
        data-testid="app-confirm"
        aria-labelledby="app-confirm-title"
      >
        {opts.title && (
          <DialogTitle id="app-confirm-title" sx={{ pb: 1 }}>
            {opts.title}
          </DialogTitle>
        )}
        <DialogContent>
          {message && (
            <DialogContentText data-testid="app-confirm-message" sx={{ whiteSpace: "pre-line" }}>
              {message}
            </DialogContentText>
          )}
          {state?.kind === "prompt" && (
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label={opts.label}
              type={opts.type || "text"}
              value={state.value}
              inputProps={{ "data-testid": "app-confirm-input", ...(opts.inputProps || {}) }}
              onChange={(e) => setState({ ...state, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") onAccept();
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} color="inherit" data-testid="app-confirm-cancel">
            {opts.cancelText}
          </Button>
          <Button
            onClick={onAccept}
            variant="contained"
            color={opts.danger ? "error" : "primary"}
            data-testid="app-confirm-ok"
            autoFocus={state?.kind !== "prompt"}
          >
            {state?.step === 2 ? "Sí, confirmar" : opts.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

ConfirmProvider.propTypes = { children: PropTypes.node };

/** Diálogo de confirmación / entrada de la app. Fuera del provider cae a «cancelar». */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (ctx) return ctx;
  return { confirm: async () => false, prompt: async () => null };
}

export default ConfirmProvider;
